import { DEMO_MODE } from "../demo/flag.js";
import { MockWsClient } from "../demo/MockWsClient.js";

type MessageHandler = (data: any) => void;
type StatusHandler = (status: "connected" | "reconnecting" | "disconnected") => void;

/**
 * WebSocket client for real-time MFP feedback streaming.
 */
export class WsClient {
  private ws: WebSocket | null = null;
  private handlers: MessageHandler[] = [];
  private statusHandlers: StatusHandler[] = [];
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private closed = false;
  private connectionState: "connected" | "reconnecting" | "disconnected" = "disconnected";

  constructor(private readonly url: string = `ws://${window.location.host}/ws`) {}

  getConnectionState(): "connected" | "reconnecting" | "disconnected" {
    return this.connectionState;
  }

  connect(): void {
    if (this.closed) return;

    // Tear down any existing connection before reconnecting
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.close();
      this.ws = null;
    }
    this.reconnectAttempts = 0;

    this.ws = new WebSocket(this.url);

    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.setConnectionState("connected");
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        for (const handler of this.handlers) {
          handler(data);
        }
      } catch {
        // Ignore invalid JSON
      }
    };

    this.ws.onclose = () => {
      if (this.closed) return;
      this.setConnectionState("reconnecting");
      this.scheduleReconnect();
    };

    this.ws.onerror = () => {
      this.ws?.close();
    };
  }

  onMessage(handler: MessageHandler): () => void {
    this.handlers.push(handler);
    return () => {
      this.handlers = this.handlers.filter((h) => h !== handler);
    };
  }

  onStatusChange(handler: StatusHandler): () => void {
    this.statusHandlers.push(handler);
    return () => {
      this.statusHandlers = this.statusHandlers.filter((h) => h !== handler);
    };
  }

  subscribe(filter: { topologyId?: string; runId?: string; types?: string[] }): void {
    this.send({ type: "subscribe", ...filter });
  }

  sendInputResponse(runId: string, feedbackMessageId: string, value: unknown): void {
    this.send({
      type: "input-response",
      runId,
      feedbackMessageId,
      value,
    });
  }

  close(): void {
    this.closed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    this.ws?.close();
    this.ws = null;
    this.setConnectionState("disconnected");
  }

  private send(data: unknown): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  private setConnectionState(state: "connected" | "reconnecting" | "disconnected"): void {
    this.connectionState = state;
    for (const handler of this.statusHandlers) {
      handler(state);
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.setConnectionState("disconnected");
      return;
    }
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    this.reconnectAttempts++;
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }
}

// Singleton instance. In the simulated build (DEMO_MODE) this is a MockWsClient
// driven by DemoSimulation instead of a real socket; consumers are unchanged.
export const wsClient = DEMO_MODE ? new MockWsClient() : new WsClient();
